import { Owner, OwnerFilter } from "@/api/owner/types";
import { Page } from "@/api/types";
import * as ownerApi from "@/api/owner/index";
import store from "@/store";
import * as mt from "@/store/owner/mutation-types";
import * as notificationService from "../notification/index";
import i18n from "@/i18n";
import * as farmService from '../farms';
import EventBus from "@/event-bus";

export async function clearOwner() {
  await store.dispatch(`owner/${mt.CLEAR_OWNER}`);
}

async function createOwner(owner: Owner) {
  try {
    const createdOwner = (await ownerApi.create(owner)).data;
    await store.dispatch(`owner/${mt.FETCH_OWNER_SUCCESS}`, createdOwner);
  } catch (err) {
    await store.dispatch(`owner/${mt.CREATE_OWNER_FAILURE}`);
  }
}

 async function updateOwner(owner: Owner) {
  try {
    const updatedOwner = (await ownerApi.update(owner)).data;
    await store.dispatch(`owner/${mt.FETCH_OWNER_SUCCESS}`, updatedOwner);
  } catch (err) {
    await store.dispatch(`owner/${mt.UPDATE_OWNER_FAILURE}`);
  }
}

export async function submitOwner(owner: Owner) {
  await store.dispatch(`owner/${mt.SUBMITING_OWNER}`);
  if (owner.uuid) {
    updateOwner(owner);
  } else {
    createOwner(owner);
  }
}

export async function fetchOwnerByOwnerKey(ownerKey: string) {
  await store.dispatch(`owner/${mt.FETCHING_OWNER_BY_KEY}`);

  try {
    const owner = (await ownerApi.getByOwnerKey(ownerKey)).data;
    await store.dispatch(`owner/${mt.FETCH_BY_KEY_SUCCESS}`, owner);
  } catch (err) {
    if (err.response && err.response.status === 404) {
      await store.dispatch(`owner/${mt.FETCH_FAILURE}`);
    }
  }
}

export async function fetchOwnerByUuid(uuid: string) {
  await store.dispatch(`owner/${mt.FETCHING_OWNER_BY_ID}`);

  try {
    let owner: Owner;
    if ((store.state as any).owner.ownerContext?.uuid === uuid) {
      owner = (store.state as any).owner.ownerContext;
    } else {
      await farmService.clearFarms();
      owner = (await ownerApi.getByUuid(uuid)).data;
    }
    store.dispatch(`owner/${mt.FETCH_BY_ID_SUCCESS}`, owner);
  } catch (err) {
    if (err.response && err.response.status === 404) {
      store.dispatch(`owner/${mt.FETCH_FAILURE}`);
    }
  }
}

export async function fetchAllByFilter(filter: OwnerFilter, page: Page) {
  await store.dispatch(`owner/${mt.FETCHING_OWNERS_BY_FILTER}`);

  try {
    const owners = await ownerApi.getAll(filter, page);
    await changeFilter(filter);
    await store.dispatch(`owner/${mt.FETCHING_OWNERS_BY_FILTER_SUCCESS}`, owners);
  } catch (err) {
    await store.dispatch(`owner/${mt.FETCH_OWNERS_FAILURE}`);
  }
}

export async function fetchAllByFilterWithoutStore(filter: OwnerFilter, page: Page) {
  return await ownerApi.getAll(filter, page);
}

export async function admit(owner: Owner, dispatch: boolean = false) {
  let updatedOwner = owner;
  try {
    updatedOwner = (await ownerApi.setAdmitted(owner.admit())).data;
    if (dispatch) {
      await store.dispatch(`owner/${mt.FETCH_OWNER_SUCCESS}`, updatedOwner);
    }
    notificationService.addSuccessNotification(
      i18n.t("notifications.success.owner-admitted-message", { name: owner.name }) as string
    );

    EventBus.$emit("update:owners");
  } catch (err) {
    if (dispatch) {
      await store.dispatch(`owner/${mt.UPDATE_OWNER_FAILURE}`);
    }
    notificationService.addErrorNotification(
      i18n.t("notifications.error.owner-admitted-message", { name: owner.name }) as string
    );
  }
  return updatedOwner;
}

export async function unadmit(owner: Owner, dispatch: boolean = false) {
  let updatedOwner = owner;
  try {
    updatedOwner = (await ownerApi.setAdmitted(owner.unadmit())).data;
    if (dispatch) {
      await store.dispatch(`owner/${mt.FETCH_OWNER_SUCCESS}`, updatedOwner);
    }
    notificationService.addWarnNotification(
      i18n.t("notifications.success.owner-unadmitted-message", { name: owner.name }) as string
    );

    EventBus.$emit("update:owners");
  } catch (err) {
    if (dispatch) {
      await store.dispatch(`owner/${mt.UPDATE_OWNER_FAILURE}`);
    }
    notificationService.addErrorNotification(
      i18n.t("notifications.error.owner-unadmitted-message", { name: owner.name }) as string
    );
  }
  return updatedOwner;
}


export async function getAdmittedCount(onlyUserOwners: boolean = false) {
  await store.dispatch(`owner/${mt.FETCHING_ADMITTED_COUNT}`);

  try {
    const admittedCount = (await ownerApi.getAdmittedCount(onlyUserOwners)).data;
    await store.dispatch(`owner/${mt.FETCH_ADMITTED_COUNT_SUCCESS}`, admittedCount);
  } catch (err) {
    if (err.response && err.response.status === 404) {
      await store.dispatch(`owner/${mt.FETCH_ADMITTED_COUNT_FAILED}`);
    }
  }
}

export async function deleteOwner(owner: Owner) {
  try {
    await ownerApi.deleteByUuid(owner.uuid!);
    await store.dispatch(`owner/${mt.CLEAR_OWNER}`);

    notificationService.addSuccessNotification(
      i18n.t("notifications.success.owner-delete", { name: owner.name }) as string
    );

    EventBus.$emit("update:owners");
  } catch (err) {
    await store.dispatch(`owner/${mt.DELETE_OWNER_FAILURE}`);

    notificationService.addErrorNotification(
      i18n.t("notifications.error.owner-delete", { name: owner.name }) as string
    );
  }
}

export async function changeFilter(filter: OwnerFilter) {
  await store.dispatch(`owner/${mt.CHANGE_FILTER_VALUE}`, filter);
}

export async function clearFilter() {
  await store.dispatch(`owner/${mt.CLEAR_FILTER}`);
  EventBus.$emit("update:owners");
}
